export interface Market {
  code: string;
  currency: string;
  symbol: string;
  locale: string;
  flag: string;
  label: string;
  name: string;
  basePriceCents: number;
  extraStyleCents: number;
  languageCode?: string;
}

export const MARKETS: Record<string, Market> = {
  usd: {
    code: "usd",
    currency: "USD",
    symbol: "$",
    locale: "en-US",
    flag: "🇺🇸",
    label: "USD",
    name: "United States",
    basePriceCents: 699,
    extraStyleCents: 100,
  },
  eur: {
    code: "eur",
    currency: "EUR",
    symbol: "€",
    locale: "de-DE",
    flag: "🇪🇺",
    label: "EUR",
    name: "Eurozone",
    basePriceCents: 649,
    extraStyleCents: 99,
  },
  gbp: {
    code: "gbp",
    currency: "GBP",
    symbol: "£",
    locale: "en-GB",
    flag: "🇬🇧",
    label: "GBP",
    name: "United Kingdom",
    basePriceCents: 549,
    extraStyleCents: 79,
  },
  sek: {
    code: "sek",
    currency: "SEK",
    symbol: "kr",
    locale: "sv-SE",
    flag: "🇸🇪",
    label: "SEK",
    name: "Sweden",
    basePriceCents: 7490,
    extraStyleCents: 1000,
  },
  nok: {
    code: "nok",
    currency: "NOK",
    symbol: "kr",
    locale: "nb-NO",
    flag: "🇳🇴",
    label: "NOK",
    name: "Norway",
    basePriceCents: 7990,
    extraStyleCents: 1100,
  },
  dkk: {
    code: "dkk",
    currency: "DKK",
    symbol: "kr",
    locale: "da-DK",
    flag: "🇩🇰",
    label: "DKK",
    name: "Denmark",
    basePriceCents: 4990,
    extraStyleCents: 700,
  },
};

export const MARKET_LIST = Object.values(MARKETS);
export const DEFAULT_MARKET = MARKETS.usd;
const STORAGE_KEY = "lp_currency";

const ZERO_DECIMAL_DISPLAY = new Set(["sek", "nok", "dkk"]);

const LOCALE_MAP: Record<string, string> = {
  // Nordic
  "sv": "sek",
  "sv-se": "sek",
  "nb": "nok",
  "nn": "nok",
  "no": "nok",
  "nb-no": "nok",
  "nn-no": "nok",
  "da": "dkk",
  "da-dk": "dkk",
  // UK
  "en-gb": "gbp",
  // Euro-zone languages
  "de": "eur",
  "de-de": "eur",
  "de-at": "eur",
  "de-ch": "eur",
  "fr": "eur",
  "fr-fr": "eur",
  "fr-be": "eur",
  "fr-ch": "eur",
  "it": "eur",
  "it-it": "eur",
  "it-ch": "eur",
  "es": "eur",
  "es-es": "eur",
  "nl": "eur",
  "nl-nl": "eur",
  "nl-be": "eur",
  "fi": "eur",
  "fi-fi": "eur",
  "pt": "eur",
  "pt-pt": "eur",
  "el": "eur",
  "el-gr": "eur",
  "pl": "eur",
  "pl-pl": "eur",
  "is": "eur",
  "is-is": "eur",
};

export function detectMarketCode(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && MARKETS[stored]) return stored;
  } catch { /* localStorage unavailable */ }

  try {
    const languages = navigator.languages || [navigator.language];
    for (const lang of languages) {
      const lower = lang.toLowerCase();
      if (LOCALE_MAP[lower]) return LOCALE_MAP[lower];
      const base = lower.split("-")[0];
      if (LOCALE_MAP[base]) return LOCALE_MAP[base];
    }
  } catch { /* navigator unavailable */ }

  return "usd";
}

export function persistMarket(code: string) {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch { /* localStorage unavailable */ }
}

export function formatPrice(amountCents: number, market: Market): string {
  const amount = amountCents / 100;
  const useZeroDecimals = ZERO_DECIMAL_DISPLAY.has(market.code) && amount % 1 === 0;
  return new Intl.NumberFormat(market.locale, {
    style: "currency",
    currency: market.currency,
    minimumFractionDigits: useZeroDecimals ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatPricePerEmployee(market: Market): string {
  return formatPrice(market.basePriceCents, market);
}

export function formatExtraStyle(market: Market): string {
  return formatPrice(market.extraStyleCents, market);
}
